import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { esmGraphNodes as dummyNodes } from "./esmGraphData";
import EsmBenefitsModal from "./EsmBenefitsModal";
import { getAdminCustomers } from "../../../apis/adminCustomers";
import { getAdminCustomerCategories } from "../../../apis/adminCustomerCategories";
import { normalizeUploadedImageUrl } from "../../../apis/uploadImage";
import ESMLogo from "../../../images/Logo/logoESM.png";
import "./EsmGraphOverview.scss";

const EsmGraphOverview = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language.startsWith("en") ? "en" : "vi";
  const isEnglish = lang === "en";
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [nodesData, setNodesData] = useState(dummyNodes);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    Promise.all([
      getAdminCustomers({ pageIndex: 1, pageSize: 100, languageCode: lang }),
      getAdminCustomerCategories({
        pageIndex: 1,
        pageSize: 100,
        languageCode: lang,
      }),
    ])
      .then(([custRes, catRes]) => {
        if (!mounted) return;

        const allCustomers =
          custRes?.items || custRes?.data?.items || custRes?.data || [];
        const categories =
          catRes?.items || catRes?.data?.items || catRes?.data || [];

        if (allCustomers.length > 0) {
          // Map database category IDs to our 6 graph nodes based on category title
          const categoryIdToNodeIndex = {};
          categories.forEach((cat) => {
            const catName = (
              cat.translations?.[0]?.name ||
              cat.name ||
              cat.title ||
              ""
            ).toLowerCase();
            if (catName.includes("bệnh viện") || catName.includes("hospital"))
              categoryIdToNodeIndex[cat.id] = 0;
            else if (
              catName.includes("bảo hiểm") ||
              catName.includes("insurance")
            )
              categoryIdToNodeIndex[cat.id] = 1;
            else if (catName.includes("dịch vụ") || catName.includes("service"))
              categoryIdToNodeIndex[cat.id] = 2;
            else if (catName.includes("ngân hàng") || catName.includes("bank"))
              categoryIdToNodeIndex[cat.id] = 3;
            else if (catName.includes("cung ứng") || catName.includes("supply"))
              categoryIdToNodeIndex[cat.id] = 4;
            else if (
              catName.includes("phòng khám") ||
              catName.includes("clinic") ||
              catName.includes("nha khoa")
            )
              categoryIdToNodeIndex[cat.id] = 5;
          });

          let newNodes = JSON.parse(JSON.stringify(dummyNodes));

          // Clear dummy brands
          newNodes.forEach((n) => (n.brands = []));

          allCustomers.forEach((cust) => {
            if (cust.logoImage && cust.customerCategoryId) {
              const imgUrl = normalizeUploadedImageUrl(cust.logoImage);
              const matchedNodeIndex =
                categoryIdToNodeIndex[cust.customerCategoryId];

              if (
                matchedNodeIndex !== undefined &&
                newNodes[matchedNodeIndex].brands.length < 7
              ) {
                newNodes[matchedNodeIndex].brands.push(imgUrl);
              }
            }
          });

          // Fill any empty nodes with dummy images to preserve the honeycomb visual if data is lacking
          newNodes.forEach((n, i) => {
            if (n.brands.length === 0) {
              n.brands = dummyNodes[i].brands;
            }
          });

          setNodesData(newNodes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(
          "Error fetching customers/categories for ESM Graph:",
          err,
        );
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [lang]);

  const handleNodeClick = (node) => {
    setSelectedNodeId(node.id);
  };

  if (isLoading) {
    return null; // Prevents the visual "pop in" effect of dummy images swapping to real ones
  }

  return (
    <div className="esm-graph-page">
      <div className="layout_container">
        <div className="esm-graph-header">
          <span>MÔ HÌNH ỨNG DỤNG</span>
          <h1>Hệ Sinh Thái ESM</h1>
          <p>
            Khám phá hệ sinh thái thông minh kết nối toàn diện Y tế và Chăm sóc
            sức khỏe
          </p>
        </div>

        <div className="esm-graph-container">
          <div
            className="esm-center-globe"
            onClick={() => {
              navigate("/tuong-tac-esm");
              window.scrollTo(0, 0);
            }}
            style={{ cursor: "pointer" }}
            title={
              isEnglish ? "View Interaction Model" : "Xem Mô Hình Tương Tác"
            }
          >
            <div className="esm-logo-inner">
              <img src={ESMLogo} alt="ESM Logo" className="esm-image-logo" />
            </div>
            {/* Connecting Lines SVG */}
            <svg className="esm-connections" viewBox="0 0 800 800">
              <circle cx="400" cy="400" r="280" className="esm-orbit-path" />
            </svg>
          </div>

          {/* 6 Nodes */}
          <div className="esm-nodes-wrapper">
            {nodesData.map((node, index) => {
              return (
                <div
                  key={node.id}
                  className={`esm-node esm-node--${index + 1}`}
                  onClick={() => handleNodeClick(node)}
                >
                  {/* Honeycomb Wrapper for Counter-Rotation */}
                  <div className="esm-honeycomb-wrapper">
                    <div className="esm-honeycomb-cluster">
                      {node.brands.map((brandImg, i) => (
                        <div
                          key={i}
                          className={`esm-hex-cell esm-hex-cell--${i}`}
                        >
                          <div className="esm-hex-inner">
                            {brandImg ? (
                              <img
                                src={brandImg}
                                alt={`Brand ${i}`}
                                loading="eager"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Label Wrapper for Counter-Rotation */}
                  <div className="esm-label-wrapper">
                    <span className="esm-node-label">
                      {isEnglish ? node.labelEn : node.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sub-modal displaying the Benefits */}
      <EsmBenefitsModal
        isOpen={!!selectedNodeId}
        selectedNodeId={selectedNodeId}
        onClose={() => setSelectedNodeId(null)}
        isEnglish={isEnglish}
      />
    </div>
  );
};

export default EsmGraphOverview;
